/**
 * perf-state — PURE display-state logic for the D44 perf row (lcp/inp/cls). No DOM, no
 * libraries: this is the deterministic state machine behind the status line, unit-tested in
 * tests/perf-state.test.ts (regola 8).
 *
 * Why this exists (device bugs 2026-09-24/25): a bare "?" that never fills looks like a bug —
 * and it hides WHICH gate fell. web-vitals onINP silently returns when the engine lacks
 * `PerformanceEventTiming.prototype.interactionId` (onINP.js gate, web-vitals PR 490),
 * and its observe() wrapper swallows every error, so an engine rejecting an out-of-spec
 * `durationThreshold: 0` (spec minimum is 16, MDN) also dies silently. Every failure mode here
 * maps to an EXPLAINED state instead of a mystery "?".
 *
 * States: number = measured value · "waiting" = capability present, not measured yet ·
 * "na-engine" = the engine cannot produce this metric · "na-iid" = event entries flow but carry
 * interactionId 0 (WebKit shipped interactionId logic late: WebKit bug 297361 / PR #49304), so
 * INP is structurally unattributable · "na-entries" = a tap completed its paint and the engine
 * produced zero event entries.
 */
export type MetricDisplay = number | "waiting" | "na-engine" | "na-iid" | "na-entries";

export interface InpInput {
  /** Same gate web-vitals uses: PerformanceEventTiming + interactionId on the prototype. */
  supported: boolean;
  /** Value reported by web-vitals (null = not reported). */
  valueMs: number | null;
  /** Raw `event` entries observed by our own counter since page load. */
  entries: number;
  /** Entries among those with interactionId !== 0. */
  attributed: number;
  /** True once a tap has completed its paint (measureReaction double-rAF). */
  tapPainted: boolean;
}

export function inpDisplay(i: InpInput): MetricDisplay {
  if (i.valueMs !== null) return i.valueMs;
  if (!i.supported) return "na-engine";
  if (i.entries > 0 && i.attributed === 0) return "na-iid";
  if (i.tapPainted && i.entries === 0) return "na-entries";
  return "waiting";
}

export function lcpDisplay(supported: boolean, valueMs: number | null): MetricDisplay {
  if (valueMs !== null) return valueMs;
  return supported ? "waiting" : "na-engine";
}

/**
 * CLS accumulates from 0: with layout-shift support and no shift measured yet, the cumulative
 * value IS 0 — not "unknown". web-vitals only reports on change, so 0 would otherwise never
 * appear (the second "permanent ?" bug class).
 */
export function clsDisplay(supported: boolean, value: number | null): MetricDisplay {
  if (value !== null) return value;
  return supported ? 0 : "na-engine";
}
